import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  const t = translations[language];

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const activeClass = "text-petrol font-semibold border-b-2 border-petrol pb-1 tracking-wider text-xs uppercase";
  const inactiveClass = "text-navy-900/80 hover:text-petrol transition-colors pb-1 border-b-2 border-transparent hover:border-bluegray tracking-wider text-xs uppercase";

  const services = [
    { name: t.nav.erp, path: '/erp-lead' },
    { name: t.nav.data, path: '/data-migration' },
    { name: t.nav.agile, path: '/agile-scrum-coach' }
  ];

  const isServiceActive = services.some(s => location.pathname === s.path);

  return (
    <header className="relative z-40 w-full bg-offwhite border-b border-bluegray">
      {/* Topbar with Contact Info */}
      <div className="bg-navy-900 text-white py-2.5 px-4 sm:px-6 lg:px-8 text-xs border-b border-navy-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <a href="tel:30655509" className="hover:text-white transition-colors flex items-center gap-2 whitespace-nowrap">
              <i className="fa-solid fa-phone text-white"></i>
              <span className="font-semibold">30 65 55 09</span>
            </a>
            <a href="mailto:rvi@insightly.dk" className="hover:text-white transition-colors flex items-center gap-2 whitespace-nowrap">
              <i className="fa-solid fa-envelope text-white"></i>
              <span className="font-semibold">rvi@insightly.dk</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white text-xs hidden md:inline uppercase tracking-widest">{t.header.specialist}</span>
            <div className="flex items-center gap-2 border-l border-navy-800 pl-4 ml-2">
              {['da', 'en', 'de'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`text-xs font-bold uppercase tracking-wider transition-colors ${
                    language === lang ? 'text-petrol' : 'text-white/60 hover:text-white'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <div className="h-10 w-10 bg-navy-900 text-offwhite flex items-center justify-center rounded-none font-serif text-xl font-bold tracking-wider group-hover:bg-petrol transition-colors duration-300">
            I
          </div>
          <div>
            <span className="font-serif text-2xl font-bold tracking-tight text-navy-900 block leading-none">Insightly</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-navy-700 font-semibold block mt-0.5">ApS · René Vilsgaard</span>
          </div>
        </Link>

        {/* Desktop Navigation (Visible >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8 text-sm font-medium">
          <NavLink to="/" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
            {t.nav.home}
          </NavLink>
          <NavLink to="/om-rene" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
            {t.nav.about}
          </NavLink>

          {/* Services Dropdown */}
          <div className="relative group">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              onMouseEnter={() => setIsDropdownOpen(true)}
              className={`flex items-center gap-1.5 cursor-pointer pb-1 border-b-2 transition-colors tracking-wider text-xs uppercase ${
                isServiceActive 
                  ? 'text-petrol font-semibold border-petrol' 
                  : 'text-navy-900/80 hover:text-petrol border-transparent'
              }`}
            >
              <span>{t.nav.services}</span>
              <i className={`fa-light fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Dropdown Menu */}
            <div
              onMouseLeave={() => setIsDropdownOpen(false)}
              className={`absolute left-0 mt-2 w-56 bg-white rounded-none shadow-xl border border-bluegray py-2 z-50 transition-all duration-200 ${
                isDropdownOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'
              }`}
            >
              {services.map((service) => (
                <NavLink
                  key={service.path}
                  to={service.path}
                  className={({ isActive }) =>
                    `block px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                      isActive
                        ? 'bg-petrol-light text-petrol font-semibold'
                        : 'text-navy-900/80 hover:bg-navy-50 hover:text-petrol'
                    }`
                  }
                >
                  {service.name}
                </NavLink>
              ))}
            </div>
          </div>

          <NavLink to="/erfaring" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
            {t.nav.experience}
          </NavLink>
          <NavLink to="/kontakt" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
            {t.nav.contact}
          </NavLink>

          {/* LinkedIn Fast CTA */}
          <a
            href="https://dk.linkedin.com/in/rene-vilsgaard/da"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-4 bg-navy-900 hover:bg-petrol text-offwhite px-5 py-3 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-sm flex items-center gap-2"
          >
            <i className="fa-brands fa-linkedin"></i>
            <span>{t.header.linkedinContact}</span>
          </a>
        </nav>

        {/* Mobile Menu Button (Visible < 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="min-[1200px]:hidden text-navy-900 hover:text-petrol p-2 focus:outline-none"
          aria-label={t.header.toggleMenu}
        >
          <i className={`fa-light ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Navigation Drawer (Visible < 1200px) */}
      <div
        className={`fixed inset-0 z-50 bg-navy-950/40 backdrop-blur-sm min-[1200px]:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      >
        <div
          className={`absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-2xl p-6 flex flex-col justify-between transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div>
            {/* Mobile Header */}
            <div className="flex justify-between items-center pb-6 border-b border-bluegray">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-navy-900 text-offwhite flex items-center justify-center rounded-none font-serif text-lg font-bold">
                  I
                </div>
                <span className="font-serif text-xl font-bold text-navy-900">Insightly</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-navy-900 hover:text-petrol p-2 focus:outline-none"
                aria-label={t.header.closeMenu}
              >
                <i className="fa-light fa-xmark text-2xl"></i>
              </button>
            </div>

            {/* Mobile Nav Links */}
            <nav className="flex flex-col gap-5 mt-8 text-sm font-bold uppercase tracking-wider">
              <NavLink
                to="/"
                className={({ isActive }) => isActive ? "text-petrol font-semibold" : "text-navy-900/80 hover:text-petrol"}
              >
                {t.nav.home}
              </NavLink>
              <NavLink
                to="/om-rene"
                className={({ isActive }) => isActive ? "text-petrol font-semibold" : "text-navy-900/80 hover:text-petrol"}
              >
                {t.nav.about}
              </NavLink>

              {/* Mobile Services Section */}
              <div className="border-t border-b border-bluegray/50 py-3 my-1">
                <span className="text-xs uppercase tracking-widest text-navy-700 font-bold block mb-3">// {t.nav.services}</span>
                <div className="flex flex-col gap-3 pl-4">
                  {services.map((service) => (
                    <NavLink
                      key={service.path}
                      to={service.path}
                      className={({ isActive }) =>
                        isActive ? "text-petrol font-semibold text-xs uppercase tracking-wider" : "text-navy-900/70 hover:text-petrol text-xs uppercase tracking-wider"
                      }
                    >
                      {service.name}
                    </NavLink>
                  ))}
                </div>
              </div>

              <NavLink
                to="/erfaring"
                className={({ isActive }) => isActive ? "text-petrol font-semibold" : "text-navy-900/80 hover:text-petrol"}
              >
                {t.nav.experience}
              </NavLink>
              <NavLink
                to="/kontakt"
                className={({ isActive }) => isActive ? "text-petrol font-semibold" : "text-navy-900/80 hover:text-petrol"}
              >
                {t.nav.contact}
              </NavLink>
            </nav>
          </div>

          {/* Mobile Footer Area */}
          <div className="border-t border-bluegray pt-6 mt-auto">
            <div className="flex flex-col gap-3 text-sm text-navy-900/70 mb-6">
              <a href="tel:30655509" className="flex items-center gap-2.5 hover:text-petrol">
                <i className="fa-solid fa-phone text-petrol"></i>
                <span className="font-semibold">30 65 55 09</span>
              </a>
              <a href="mailto:rvi@insightly.dk" className="flex items-center gap-2.5 hover:text-petrol">
                <i className="fa-solid fa-envelope text-petrol"></i>
                <span className="font-semibold">rvi@insightly.dk</span>
              </a>
              <div className="flex items-center gap-2.5">
                <i className="fa-solid fa-location-dot text-petrol"></i>
                <span className="font-semibold">{t.header.location}</span>
              </div>
            </div>

            <a
              href="https://dk.linkedin.com/in/rene-vilsgaard/da"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-navy-900 hover:bg-petrol text-offwhite py-3.5 rounded-none text-center font-bold uppercase tracking-wider text-xs transition-all duration-300 flex items-center justify-center gap-2 shadow-sm"
            >
              <i className="fa-brands fa-linkedin text-lg"></i>
              <span>{t.header.linkedinFollow}</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
